import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength, alpha, alphaNum, email, numeric, helpers } from '@vuelidate/validators'

export default class FieldVals {
    constructor(appointment) {

        // hold reference to appointment to be used in conditional vals
        this.appt = appointment

        // This holds the validation configuration based on appt type
        this.config = {}

        // These are field counts used to drive the completion pie chart
        this.completion = {}

        // Based on the type of appointment, create a validation config object
        // to be used for the appointment application.
        //
        // Types:
        //    Individual
        //    Corporate
        //    Both Corporate and an extra Rep writing number
        //    Representative
        //
       let type = appointment.appointmentTypeCode
        switch (type) {
            case 'APPTTYP_WVB':
            case 'APPTTYP_IND':
                this.config = this.#IndividualValConfig();
                // personal = 11 + (15 answers) + (4 address flds) + (2 sigs) + (4 IRS)
                // comms = (3 sigs) +  1 remit typ + (5 banking)
                // fees =  1 state min + 1 sig
                // agrees = 4 sigs
                // docs = 10
                this.completion = this.#IndividualCompletion();
                break
           case 'APPTTYP_CORP':
           case 'APPTTYP_VB_BOTH':
           case 'APPTTYP_VB_CORP':
                this.config = this.#CorporateValConfig
                // personal = 14 + (15 answers) + (8 address flds) + (2 sigs) + (4 IRS)
                // comms = (3 sigs) +  1 remit typ + (5 banking)
                // fees =  1 state min + 1 sig
                // agrees = 4 sigs
                // docs = 10
                this.completion = { personal: 43, comms: 9, fees: 2, agrees: 4, docs: this.appt.willFax ? 10 : 13 }
                break
            case 'APPTTYP_BOTH':
                this.config = this.#CorpPlusRepValConfig
                // personal = 14 + (15 answers) + (8 address flds) + (2 sigs) + (4 IRS)
                // comms = (3 sigs) +  1 remit typ + (5 banking)
                // fees =  1 state min + 1 sig
                // agrees = 4 sigs
                // docs = 12
                this.completion = { personal: 43, comms: 9, fees: 2, agrees: 4, docs: this.appt.willFax ? 12 : 15 }
                break
           case 'APPTTYP_REP':
           case 'APPTTYP_VB_REP':
                this.config = this.#RepresentativeValConfig
                // personal = 12 + (15 answers) + (4 address flds) + (2 sigs)
                // comms = (3 sigs) +  1 remit typ + (5 banking)
                // fees =  1 state min + 1 sig
                // agrees = 4 sigs
                // docs = 12
                this.completion = { personal: 33, comms: 9, fees: 2, agrees: 4, docs: this.appt.willFax ? 10 : 13 }
                break
            case 'APPTTYP_PRIN':
                this.config = this.#PrincipalValConfig
                // personal = 12 + (15 answers) + (4 address flds) + (2 sigs)
                // comms = (3 sigs) +  1 remit typ + (5 banking)
                // fees =  1 state min + 1 sig
                // agrees = 4 sigs
                // docs = 12
                this.completion = { personal: 33, comms: 9, fees: 2, agrees: 4, docs: this.appt.willFax ? 10 : 13 }
                break
            default: throw new Error(`Unsupported appointment type: ${type}`);
        }


        // TODO: Get this from the database
        this.appFields = [
            { key: 'nationalProducerNumber', page: 'PERSONAL', elementName: 'personal.nationalProducerNumber' },
            { key: 'lastName', page: 'PERSONAL', elementName: 'personal.lastName' },
            { key: 'firstName', page: 'PERSONAL', elementName: 'personal.firstName' },
            { key: 'middleName', page: 'PERSONAL', elementName: 'personal.middleName' },
            { key: 'ssn', page: 'PERSONAL', elementName: 'personal.ssn' },
            { key: 'birthDate', page: 'PERSONAL', elementName: 'personal.birthDate' },
            { key: 'emailAddress', page: 'PERSONAL', elementName: 'personal.emailAddress' },
            { key: 'driverLicenseNumber', page: 'PERSONAL', elementName: 'personal.driverLicenseNumber' },
            { key: 'residentStateInsuranceLicenseNumber', page: 'PERSONAL', elementName: 'personal.residentStateInsuranceLicenseNumber' },
            { key: 'cellNumber', page: 'PERSONAL', elementName: 'personal.cellNumber' },
            { key: 'businessName', page: 'PERSONAL', elementName: 'personal.businessName' },
            { key: 'businessCellNumber', page: 'PERSONAL', elementName: 'personal.businessCellNumber' },
            { key: 'businessTaxID', page: 'PERSONAL', elementName: 'personal.businessTaxID' },
            { key: 'businessFaxNumber2', page: 'PERSONAL', elementName: 'personal.businessFaxNumber2' },
            { key: 'businessFaxNumber', page: 'PERSONAL', elementName: 'personal.businessFaxNumber' },
            { key: 'sendCorrespondenceToCode', page: 'PERSONAL', elementName: 'personal.sendCorrespondenceToCode' },
            { key: 'commissionRemitTypeCode', page: 'COMMS', elementName: 'commissionRemitTypeCode' },
            { key: 'name', page: 'PERSONAL', elementName: 'apptIRSInfo.name' },
            { key: 'businessTypeCode', page: 'PERSONAL', elementName: 'apptIRSInfo.businessTypeCode' },
            { key: 'w9BusinessName', page: 'PERSONAL', elementName: 'apptIRSInfo.businessName' },
            { key: 'states', page: 'FEES', elementName: 'states' },
            { key: 'stateFeeQuestion', page: 'FEES', elementName: 'stateFeeQuestion' },
            { key: 'answers', page: 'PERSONAL', elementName: 'personal.answers' },
            { key: 'hasSigQA', page: 'PERSONAL', elementName: 'qaSig' },
            { key: 'hasSigW9', page: 'PERSONAL', elementName: 'w9Sig' },
            { key: 'hasSigSched', page: 'COMMS', elementName: 'commmName' },
            { key: 'hasSigSchedDt', page: 'COMMS', elementName: 'sigDate' },
            { key: 'hasSigAgt', page: 'COMMS', elementName: 'comAgentName' },
            { key: 'hasSigFees', page: 'FEES', elementName: 'feeMmName' },
            { key: 'hasSigPolicy', page: 'AGREEMENTS', elementName: 'chkTwo' },
            { key: 'hasSigAgent', page: 'AGREEMENTS', elementName: 'chkOne' },
            { key: 'hasSigRep', page: 'AGREEMENTS', elementName: 'chkNine' },
            { key: 'hasSigAML', page: 'AGREEMENTS', elementName: 'chkThree' },
            { key: 'hasSigName', page: 'AGREEMENTS', elementName: 'yourName' },
            { key: 'hasSigAgree', page: 'AGREEMENTS', elementName: 'mmName' },
            { key: 'hasDoc1', page: 'DOCS', elementName: 'DOCTYPSTATELIC' },
            { key: 'hasDoc2', page: 'DOCS', elementName: 'DOCTYPEOCERT' },
            { key: 'hasDoc3', page: 'DOCS', elementName: 'DOCTYPDRVLIC' },
            { key: 'homeAddress.address1', page: 'PERSONAL', elementName: 'homeAddress.address1' },
            { key: 'homeAddress.city', page: 'PERSONAL', elementName: 'homeAddress.city' },
            { key: 'homeAddress.stateCode', page: 'PERSONAL', elementName: 'homeAddress.stateCode' },
            { key: 'homeAddress.zipCode', page: 'PERSONAL', elementName: 'homeAddress.zipCode' },
            { key: 'bizAddress.address1', page: 'PERSONAL', elementName: 'bizAddress.address1' },
            { key: 'bizAddress.city', page: 'PERSONAL', elementName: 'bizAddress.city' },
            { key: 'bizAddress.stateCode', page: 'PERSONAL', elementName: 'bizAddress.stateCode' },
            { key: 'bizAddress.zipCode', page: 'PERSONAL', elementName: 'bizAddress.zipCode' },
            { key: 'bankName', page: 'COMMS', elementName: 'bankInfo.bankName' },
            { key: 'accountNumber', page: 'COMMS', elementName: 'bankInfo.accountNumber' },
            { key: 'routingNumber', page: 'COMMS', elementName: 'bankInfo.routingNumber' },
            { key: 'accountName', page: 'COMMS', elementName: 'bankInfo.accountName' },
            { key: 'accountTypeCode', page: 'COMMS', elementName: 'acctTypeCode' },
            { key: 'creditCardTypeCode', page: 'FEES', elementName: 'ccInfo.creditCardTypeCode' },
            { key: 'cardNumber', page: 'FEES', elementName: 'ccInfo.cardNumber' },
            { key: 'cvcNumber', page: 'FEES', elementName: 'ccInfo.cvcNumber' },
            { key: 'expirationMonth', page: 'FEES', elementName: 'ccInfo.expirationMonth' },
            { key: 'expirationYear', page: 'FEES', elementName: 'expirationYear' },
            { key: 'nameonCard', page: 'FEES', elementName: 'ccInfo.nameonCard' },
            { key: 'billingAddress', page: 'FEES', elementName: 'ccInfo.billingAddress' },
            { key: 'city', page: 'FEES', elementName: 'ccInfo.city' },
            { key: 'stateCode', page: 'FEES', elementName: 'ccInfo.stateCode' },
            { key: 'zip', page: 'FEES', elementName: 'ccInfo.zip' },
            { key: 'commissionRemitAssign.ssnTaxID', page: 'COMMS', elementName: 'commissionRemitAssign.ssnTaxID' },
            { key: 'commissionRemitAssign.agentAgencyName', page: 'COMMS', elementName: 'commissionRemitAssign.agentAgencyName' },
            { key: 'commissionRemitAssign.address1', page: 'COMMS', elementName: 'commissionRemitAssign.address1' },
            { key: 'commissionRemitAssign.city', page: 'COMMS', elementName: 'commissionRemitAssign.city' },
            { key: 'commissionRemitAssign.stateCode', page: 'COMMS', elementName: 'commissionRemitAssign.stateCode' },
            { key: 'commissionRemitAssign.zip', page: 'COMMS', elementName: 'commissionRemitAssign.zip' }
        ]

        // Armed with the validation field path, we need to know what page a field is on and the actual name of the input control itself
        this.locateField = function (fld) {
            return this.appFields.find(f => f.key == fld, {})
        }
    }

    // ---------------------------------------------------------------------------
    // SERVICE METHODS
    // ---------------------------------------------------------------------------

    typeOfBusCorp = (value) => this.TypeOfBusinessCorpIsValid(value)
    typeOfBusInd = (value) => this.TypeOfBusinessIndIsValid(value)
    accountNumber = (value) => this.BankAcctNumberIsValid(value)
    routingNumber = (value) => this.RoutingNumberIsValid(value)
    containsDoc = (param) => (value) => this.ContainsDocument(value, param)
    containsAddress = (param) => (value) => this.ContainsAddress(value, param)
    containsSig = (param) => (value) => this.ContainsSignature(value, param)
    allAnswered = (param) => (value) => this.AllQuestionsAnswered(value, param)
    bankReqd = (value) => this.BankInfoRequired(value)
    taxIdNotEqualsSsn = (value) => this.TaxIdNotEqualsSsn(value);
    stateFeeQuestion = (value) => this.IsStateFeeQuestionAnswered(value)
    stateFeeQuestionValid = (value) => this.IsStateFeeQuestionValid(value)

    TypeOfBusinessCorpIsValid(valu) {

        if (!this.appt) return false
       if (this.appt.appointmentTypeCode == 'APPTTYP_CORP' || this.appt.appointmentTypeCode == 'APPTTYP_VB_CORP' || this.appt.appointmentTypeCode == 'APPTTYP_VB_BOTH') {
            if (!valu || valu.length < 0) return false
            if (valu == 'W9BIZ_TYP_CCORP' || valu == 'W9BIZ_TYP_SCORP' || valu == 'W9BIZ_TYP_LLC' || valu == 'W9BIZ_TYP_OTHER') {
                return true
            } else {
                return false
            }
        }
        return true
    }

    TypeOfBusinessIndIsValid(valu) {

        if (!this.appt) return false
       if (this.appt.appointmentTypeCode == 'APPTTYP_IND' || this.appt.appointmentTypeCode == 'APPTTYP_WVB') {
            if (!valu || valu.length < 0) return false
            if (valu != 'W9BIZ_TYP_IND') {
                return false
            } else {
                return true
            }
        }
        return true
    }

    BankInfoRequired(valu) {
        if (!this.appt) return false
        if (this.appt.commissionRemitTypeCode == 'COMRMT_TYP_DIRECT') {
            if (!valu || valu.length < 1) return false
        }
        return true
    }

    BankAcctNumberIsValid(acctNo) {
        if (!acctNo || acctNo.length == 0) {
            return true
        }

        // all valid bank account numbers are 6-17 characters in length
        if (acctNo.length < 6 || acctNo.length > 17) {
            return false;
        }

        // Per Lee Ann (9/21/17) she wants to restrict acctNo to digits only
        if (isNaN(acctNo)) {
            return false;
        }

        return true;
    }

    RoutingNumberIsValid(routing) {
        if (!routing || routing.length == 0) {
            return true
        }

        // all valid routing numbers are 9 numbers in length
        if (routing.length !== 9) {
            return false;
        }

        // if it aint a number, it aint a routin' number
        if (isNaN(routing)) {
            return false;
        }

        // routing numbers starting with 5 are internal routing numbers
        // usually found on bank deposit slips
        if (routing[0] == '5') {
            return false;
        }

        // http://en.wikipedia.org/wiki/Routing_transit_number#MICR_Routing_number_format
        var checksumTotal = (7 * (parseInt(routing.charAt(0), 10) + parseInt(routing.charAt(3), 10) + parseInt(routing.charAt(6), 10))) +
            (3 * (parseInt(routing.charAt(1), 10) + parseInt(routing.charAt(4), 10) + parseInt(routing.charAt(7), 10))) +
            (9 * (parseInt(routing.charAt(2), 10) + parseInt(routing.charAt(5), 10) + parseInt(routing.charAt(8), 10)));

        var checksumMod = checksumTotal % 10;
        if (checksumMod !== 0) {
            return false;
        } else {
            return true;
        }
    }

    // C U S T O M  Validation routines
    // Return whether or not the user has provided the type of document
    ContainsDocument(arr, docType) {
        if (this.appt &&
            this.appt.willFax == true &&
            (docType == 'DOCTYP_STATE_LIC' || docType == 'DOCTYP_EO_CERT' || docType == 'DOCTYP_DRV_LIC')) {
            return true
        }
        let doc = (arr != null && arr.length > 0) ? arr.find(o => o.documentTypeCode == docType) : null
        if (doc) return true
        return false
    }

    ContainsAddress(arr, addressTypeCode) {
        let adr = arr.find(o => o.addressTypeCode == addressTypeCode)
        if (adr) return true
        return false
    }

    ContainsSignature(arr, sigTypeCode) {
        let sig = arr.find(o => o.signatureTypeCode == sigTypeCode)
        if (!sig) return false
        let result = true
        switch (sigTypeCode) {
            case 'SIGTYP_QNA':
            case 'SIGTYP_W9_CERT':
            case 'SIGTYP_COMSCHED':
            case 'SIGTYP_COMSCHED_AGT':
            case 'SIGTYP_ST_FEES':
            case 'SIGTYP_AGREENAME':
            case 'SIGTYP_AGREESIG':
                if (sig.signatureValue.length > 200 || sig.signatureValue.length < 1) result = false
                break
            case 'SIGTYP_COMSCHED_DT':
                sig.signatureValue != "" && sig.signatureValue.toLowerCase() != "invalid date" && !isNaN(Date.parse(sig.signatureValue)) ? result = true : result = false
                break
            case 'SIGTYP_POLICY_AGREE':
            case 'SIGTYP_AGENT_AGREE':
            case 'SIGTYP_ACKREP':
            case 'SIGTYP_AML':
                if (sig.signatureValue != 'Y') result = false
                break
            default: //unknown sig type - do not fail out
                result = true
        }
        return result
    }

    AllQuestionsAnswered(arr, requiredCount) {
        let found = 0
        if (!arr || arr.length < requiredCount) {
            return false
        }
        arr.forEach(answer => {
           if (answer.answerBoolean !== undefined && answer.answerBoolean != null && answer.questionType == "QUES_TYP_PERSONAL") {
                if (answer.answerBoolean == true || answer.answerBoolean == false) {
                    found++
                }
            }
        })
        return (found == requiredCount)
    }

    TaxIdNotEqualsSsn(taxId) {
        if (taxId && this.appt.personal.ssn) {
            return taxId !== this.appt.personal.ssn;
        }
        return true;
    }

   IsStateFeeQuestionAnswered(val) {
      return this.StateFeeAnswer() != null;
   }
   IsStateFeeQuestionValid(val) {
      return !this.StateFeeAnswer() || this.appt.isPA || this.appt.isMT;
   }
   StateFeeAnswer() {
      const sf = this.appt.answers.find(a => a.questionType == "QUES_TYP_STATE_FEE");
      console.warn(this.appt.answers,sf)
      return (sf && sf.answerBoolean != null) ? sf.answerBoolean : null;
   }

    // ---------------------------------------------------------------------------
    // PRIVATE MEMBERS - Validation Configuration
    // ---------------------------------------------------------------------------

    #IndividualCompletion = () => ({ personal: 36, comms: 9, fees: 2, agrees: 4, docs: this.appt.willFax ? 10 : 13 });
    #IndividualValConfig = () => ({
        a: {
            appointment: {
                personal: {
                    lastName: { required: helpers.withMessage('Last Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
                    firstName: { required: helpers.withMessage('First Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
                    middleName: { maxLength: maxLength(1) },
                    ssn: { required: helpers.withMessage('SSN is required', required), numeric, maxLength: maxLength(9), minLength: minLength(9) },
                    birthDate: { required: helpers.withMessage('Birth Date is required', required), maxValue: helpers.withMessage('Birth Date is out of range', value => value < new Date().toISOString()) },
                    emailAddress: { required: helpers.withMessage('Email Address is required', required), email },
                    nationalProducerNumber: { required: helpers.withMessage('NPN is required', required), maxLength: maxLength(25), minLength: minLength(1) },
                    residentStateInsuranceLicenseNumber: { required: helpers.withMessage('License Number is required', required), maxLength: maxLength(25), minLength: minLength(1) },
                    cellNumber: { required: helpers.withMessage('Phone Number is required', required), numeric, maxLength: maxLength(10), minLength: minLength(10) },
                    sendCorrespondenceToCode: { required: helpers.withMessage('Correspondence selection is required', required) }
                },
                commissionRemitTypeCode: { required: helpers.withMessage('Selecting a Remittance type is Required', required) },
                states: { required: helpers.withMessage('At least one state must be chosen', required), minLength: minLength(1) },

                // TODO: Drive this from the database or Change this minimum to match the number of questions
                answers: { required: helpers.withMessage('All questions must be answered', this.allAnswered(15)) },
                appUserDocuments: {
                    hasDoc2: helpers.withMessage('Missing E&O Certificate', this.containsDoc('DOCTYP_EO_CERT'))
                },
                bankInfo: {
                    bankName: { required: helpers.withMessage('Bank Name is required', this.bankReqd), maxLength: maxLength(100), minLength: minLength(2) },
                    accountNumber: {
                        required: helpers.withMessage('Account Number is required', this.bankReqd),
                        maxLength: helpers.withMessage('Account Number 17 chars max', maxLength(17)),
                        minLength: helpers.withMessage('Account Number 6 chars minimum', minLength(6)),
                        validAccount: helpers.withMessage('Account Number is Invalid', this.BankAcctNumberIsValid)
                    },
                    routingNumber: {
                        required: helpers.withMessage('Routing Number is required', this.bankReqd),
                        maxLength: helpers.withMessage('Routing Number must be 9 digits', maxLength(9)),
                        minLength: helpers.withMessage('Routing Number must be 9 digits', minLength(9)),
                        validRoute: helpers.withMessage('Routing Number is invalid', this.RoutingNumberIsValid)
                    },
                    accountName: { required: helpers.withMessage('Account Name is required', this.bankReqd), maxLength: maxLength(100), minLength: minLength(2) },
                    accountTypeCode: { required: helpers.withMessage('Bank Account Type is required', this.bankReqd) }
                },
                signatures: {
                    hasSigQA: helpers.withMessage('Q&A Signature Required', this.containsSig('SIGTYP_QNA')),
                    hasSigW9: helpers.withMessage('W-9 Signature Required', this.containsSig('SIGTYP_W9_CERT')),
                    hasSigSched: helpers.withMessage('Commission Sched Signature Required', this.containsSig('SIGTYP_COMSCHED')),
                    hasSigFees: helpers.withMessage('Fees Signature Required', this.containsSig('SIGTYP_ST_FEES')),
                    hasSigPolicy: helpers.withMessage('Policy Agreement checkbox Required', this.containsSig('SIGTYP_POLICY_AGREE')),
                    hasSigAgent: helpers.withMessage('Agent Agreement checkbox Required', this.containsSig('SIGTYP_AGENT_AGREE')),
                    hasSigAML: helpers.withMessage('AML Agreement checkbox Required', this.containsSig('SIGTYP_AML')),
                    hasSigSchedDt: helpers.withMessage('Commission Sign Date is Required', this.containsSig('SIGTYP_COMSCHED_DT')),
                    hasSigAgt: helpers.withMessage('Commission Agent Name is Required', this.containsSig('SIGTYP_COMSCHED_AGT')),
                    hasSigName: helpers.withMessage('AML: Your Name Required', this.containsSig('SIGTYP_AGREENAME')),
                    hasSigAgree: helpers.withMessage('Agreement Signature Required', this.containsSig('SIGTYP_AGREESIG'))
                },
                apptIRSInfo: {
                    name: { required: helpers.withMessage('Name (on W-9) is required', required), maxLength: maxLength(100), minLength: minLength(2) },
                    businessName: { maxLength: maxLength(100), minLength: minLength(2) },
                    businessTypeCode: {
                        required: helpers.withMessage('Business Type is required', required),
                        validApptTypeInd: helpers.withMessage('Individual\'s Appointment Type requires "Individual or Sole-Proprietorship"\'s Type Of Business value', this.typeOfBusInd),
                        validApptTypeCorp: helpers.withMessage('Corporate\'s Appointment Type requires "C Corporation" or "S Corporation"\'s Type Of Business value', this.typeOfBusCorp)
                    }
                },
                stateFeeQuestion: {
                   answer: helpers.withMessage('State Fee Question is Required', this.stateFeeQuestion),
                   valid: helpers.withMessage('At least one State Fee needs to be selected', this.stateFeeQuestionValid)
                },
            }
        },
        homeAddress: {
            address1: { required: helpers.withMessage('Home Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
            city: { required: helpers.withMessage('Home City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
            stateCode: { required: helpers.withMessage('Home State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
            zipCode: { required: helpers.withMessage('Home ZipCode is required', required), numeric, maxLength: maxLength(10), minLength: minLength(5) }
        },
        bizAddress: {
            address1: { required: helpers.withMessage('Business Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
            city: { required: helpers.withMessage('Business City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
            stateCode: { required: helpers.withMessage('Business State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
            zipCode: { required: helpers.withMessage('Business ZipCode is required', required), numeric, maxLength: maxLength(10), minLength: minLength(5) },
        }
    })

    #CorporateValConfig = {
        a: {
            appointment: {
                personal: {
                    lastName: { required: helpers.withMessage('Last Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
                    firstName: { required: helpers.withMessage('First Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
                    middleName: { maxLength: maxLength(1) },
                    ssn: { required: helpers.withMessage('SSN is required', required), numeric, maxLength: maxLength(9), minLength: minLength(9) },
                    birthDate: { required: helpers.withMessage('Birth Date is required', required), maxValue: helpers.withMessage('Birth Date is out of range', value => value < new Date().toISOString()) },
                    emailAddress: { required: helpers.withMessage('Email Address is required', required), email },
                    nationalProducerNumber: { required: helpers.withMessage('NPN is required', required), maxLength: maxLength(25), minLength: minLength(1) },
                    residentStateInsuranceLicenseNumber: { required: helpers.withMessage('License Number is required', required), maxLength: maxLength(25), minLength: minLength(1) },
                    cellNumber: { required: helpers.withMessage('Phone Number is required', required), numeric, maxLength: maxLength(10), minLength: minLength(10) },
                    businessName: { required: helpers.withMessage('Business Name is required', required), maxLength: maxLength(40), minLength: minLength(1) },
                    businessCellNumber: { required: helpers.withMessage('Business Phone is required', required), numeric, maxLength: maxLength(10), minLength: minLength(10) },
                    businessTaxID: {
                        required: helpers.withMessage('Business Tax Id is required', required), numeric, maxLength: maxLength(9), minLength: minLength(9),
                        taxIdNotEqualsSsn: helpers.withMessage("SSN and Tax Id should not be same", this.taxIdNotEqualsSsn)
                    },
                    sendCorrespondenceToCode: { required: helpers.withMessage('Correspondence selection is required', required) }
                },
                commissionRemitTypeCode: { required: helpers.withMessage('Selecting a Remittance type is Required', required) },
                states: { required: helpers.withMessage('At least one state must be chosen', required), minLength: minLength(1) },

                // TODO: Drive this from the database or Change this minimum to match the number of questions
                answers: { required: helpers.withMessage('All questions must be answered', this.allAnswered(15)) },
                appUserDocuments: {
                    hasDoc2: helpers.withMessage('Missing E&O Certificate', this.containsDoc('DOCTYP_EO_CERT'))
                },
                bankInfo: {
                    bankName: { required: helpers.withMessage('Bank Name is required', this.bankReqd), maxLength: maxLength(100), minLength: minLength(2) },
                    accountNumber: {
                        required: helpers.withMessage('Account Number is required', this.bankReqd),
                        maxLength: helpers.withMessage('Account Number 17 chars max', maxLength(17)),
                        minLength: helpers.withMessage('Account Number 6 chars minimum', minLength(6)),
                        validAccount: helpers.withMessage('Account Number is Invalid', this.BankAcctNumberIsValid)
                    },
                    routingNumber: {
                        required: helpers.withMessage('Routing Number is required', this.bankReqd),
                        maxLength: helpers.withMessage('Routing Number must be 9 digits', maxLength(9)),
                        minLength: helpers.withMessage('Routing Number must be 9 digits', minLength(9)),
                        validRoute: helpers.withMessage('Routing Number is invalid', this.RoutingNumberIsValid)
                    },
                    accountName: { required: helpers.withMessage('Account Name is required', this.bankReqd), maxLength: maxLength(100), minLength: minLength(2) },
                    accountTypeCode: { required: helpers.withMessage('Bank Account Type is required', this.bankReqd) }
                },
                signatures: {
                    hasSigQA: helpers.withMessage('Q&A Signature Required', this.containsSig('SIGTYP_QNA')),
                    hasSigW9: helpers.withMessage('W-9 Signature Required', this.containsSig('SIGTYP_W9_CERT')),
                    hasSigSched: helpers.withMessage('Commission Sched Signature Required', this.containsSig('SIGTYP_COMSCHED')),
                    hasSigFees: helpers.withMessage('Fees Signature Required', this.containsSig('SIGTYP_ST_FEES')),
                    hasSigPolicy: helpers.withMessage('Policy Acknowledge checkbox Required', this.containsSig('SIGTYP_POLICY_AGREE')),
                    hasSigAgent: helpers.withMessage('Agent Acknowledge checkbox Required', this.containsSig('SIGTYP_AGENT_AGREE')),
                    hasSigRep: helpers.withMessage('Representative Agreement checkbox Required', this.containsSig('SIGTYP_ACKREP')),
                    hasSigAML: helpers.withMessage('AML Acknowledge checkbox Required', this.containsSig('SIGTYP_AML')),
                    hasSigSchedDt: helpers.withMessage('Commission Sign Date is Required', this.containsSig('SIGTYP_COMSCHED_DT')),
                    hasSigAgt: helpers.withMessage('Commission Agent Name is Required', this.containsSig('SIGTYP_COMSCHED_AGT')),
                    hasSigName: helpers.withMessage('AML: Your Name Required', this.containsSig('SIGTYP_AGREENAME')),
                    hasSigAgree: helpers.withMessage('Agreement Signature Required', this.containsSig('SIGTYP_AGREESIG'))
                },
                apptIRSInfo: {
                    name: { required: helpers.withMessage('Name (on W-9) is required', required), maxLength: maxLength(100), minLength: minLength(2) },
                    businessName: { required: helpers.withMessage('Business Name (on W-9) is required', required), maxLength: maxLength(100), minLength: minLength(2) },
                    businessTypeCode: {
                        required: helpers.withMessage('Business Type is required', required),
                        validApptTypeInd: helpers.withMessage('Individual\'s Appointment Type requires "Individual or Sole-Proprietorship"\'s Type Of Business value', this.typeOfBusInd),
                        validApptTypeCorp: helpers.withMessage('Corporate\'s Appointment Type requires "C Corporation" or "S Corporation"\'s Type Of Business value', this.typeOfBusCorp)
                    }
              },
                stateFeeQuestion: {
                   answer: helpers.withMessage('State Fee Question is Required', this.stateFeeQuestion),
                   valid: helpers.withMessage('At least one State Fee needs to be selected', this.stateFeeQuestionValid)
                },
            }
        },
        homeAddress: {
            address1: { required: helpers.withMessage('Home Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
            city: { required: helpers.withMessage('Home City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
            stateCode: { required: helpers.withMessage('Home State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
            zipCode: { required: helpers.withMessage('Home ZipCode is required', required), numeric, maxLength: maxLength(10), minLength: minLength(5) }
        },
        bizAddress: {
            address1: { required: helpers.withMessage('Business Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
            city: { required: helpers.withMessage('Business City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
            stateCode: { required: helpers.withMessage('Business State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
            zipCode: { required: helpers.withMessage('Business ZipCode is required', required), numeric, maxLength: maxLength(10), minLength: minLength(5) },
        }
    }

    #CorpPlusRepValConfig = {
        a: {
            appointment: {
                personal: {
                    lastName: { required: helpers.withMessage('Last Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
                    firstName: { required: helpers.withMessage('First Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
                    middleName: { maxLength: maxLength(1) },
                    ssn: { required: helpers.withMessage('SSN is required', required), numeric, maxLength: maxLength(9), minLength: minLength(9) },
                    birthDate: { required: helpers.withMessage('Birth Date is required', required), maxValue: helpers.withMessage('Birth Date is out of range', value => value < new Date().toISOString()) },
                    emailAddress: { required: helpers.withMessage('Email Address is required', required), email },
                    nationalProducerNumber: { required: helpers.withMessage('NPN is required', required), maxLength: maxLength(25), minLength: minLength(1) },
                    residentStateInsuranceLicenseNumber: { required: helpers.withMessage('License Number is required', required), maxLength: maxLength(25), minLength: minLength(1) },
                    cellNumber: { required: helpers.withMessage('Phone Number is required', required), numeric, maxLength: maxLength(10), minLength: minLength(10) },
                    businessName: { required: helpers.withMessage('Business Name is required', required), maxLength: maxLength(40), minLength: minLength(1) },
                    businessCellNumber: { required: helpers.withMessage('Business Phone is required', required), numeric, maxLength: maxLength(10), minLength: minLength(10) },
                    businessTaxID: {
                        required: helpers.withMessage('Business Tax Id is required', required), numeric, maxLength: maxLength(9), minLength: minLength(9),
                        taxIdNotEqualsSsn: helpers.withMessage("SSN and Tax Id should not be same", this.taxIdNotEqualsSsn)
                    },
                    sendCorrespondenceToCode: { required: helpers.withMessage('Correspondence selection is required', required) }
                },
                commissionRemitTypeCode: { required: helpers.withMessage('Selecting a Remittance type is Required', required) },
                states: { required: helpers.withMessage('At least one state must be chosen', required), minLength: minLength(1) },

                // TODO: Drive this from the database or Change this minimum to match the number of questions
                answers: { allAnswered: helpers.withMessage('All questions must be answered', this.allAnswered(15)) },
                appUserDocuments: {
                    hasDoc2: helpers.withMessage('Missing E&O Certificate', this.containsDoc('DOCTYP_EO_CERT'))
                },
                bankInfo: {
                    bankName: { required: helpers.withMessage('Bank Name is required', this.bankReqd), maxLength: maxLength(100), minLength: minLength(2) },
                    accountNumber: {
                        required: helpers.withMessage('Account Number is required', this.bankReqd),
                        maxLength: helpers.withMessage('Account Number 17 chars max', maxLength(17)),
                        minLength: helpers.withMessage('Account Number 6 chars minimum', minLength(6)),
                        validAccount: helpers.withMessage('Account Number is Invalid', this.BankAcctNumberIsValid)
                    },
                    routingNumber: {
                        required: helpers.withMessage('Routing Number is required', this.bankReqd),
                        maxLength: helpers.withMessage('Routing Number must be 9 digits', maxLength(9)),
                        minLength: helpers.withMessage('Routing Number must be 9 digits', minLength(9)),
                        validRoute: helpers.withMessage('Routing Number is invalid', this.RoutingNumberIsValid)
                    },
                    accountName: { required: helpers.withMessage('Account Name is required', this.bankReqd), maxLength: maxLength(100), minLength: minLength(2) },
                    accountTypeCode: { required: helpers.withMessage('Bank Account Type is required', this.bankReqd) }
                },
                signatures: {
                    hasSigQA: helpers.withMessage('Q&A Signature Required', this.containsSig('SIGTYP_QNA')),
                    hasSigW9: helpers.withMessage('W-9 Signature Required', this.containsSig('SIGTYP_W9_CERT')),
                    hasSigSched: helpers.withMessage('Commission Sched Signature Required', this.containsSig('SIGTYP_COMSCHED')),
                    hasSigFees: helpers.withMessage('Fees Signature Required', this.containsSig('SIGTYP_ST_FEES')),
                    hasSigPolicy: helpers.withMessage('Policy Acknowledge checkbox Required', this.containsSig('SIGTYP_POLICY_AGREE')),
                    hasSigAgent: helpers.withMessage('Agent Agreement checkbox Required', this.containsSig('SIGTYP_AGENT_AGREE')),
                    hasSigRep: helpers.withMessage('Representative Agreement checkbox Required', this.containsSig('SIGTYP_ACKREP')),
                    hasSigAML: helpers.withMessage('AML Acknowledge checkbox Required', this.containsSig('SIGTYP_AML')),
                    hasSigSchedDt: helpers.withMessage('Commission Sign Date is Required', this.containsSig('SIGTYP_COMSCHED_DT')),
                    hasSigAgt: helpers.withMessage('Commission Agent Name is Required', this.containsSig('SIGTYP_COMSCHED_AGT')),
                    hasSigName: helpers.withMessage('AML: Your Name Required', this.containsSig('SIGTYP_AGREENAME')),
                    hasSigAgree: helpers.withMessage('Agreement Signature Required', this.containsSig('SIGTYP_AGREESIG'))
                },
                apptIRSInfo: {
                    name: { required: helpers.withMessage('Name (on W-9) is required', required), maxLength: maxLength(100), minLength: minLength(2) },
                    businessName: { required: helpers.withMessage('Business Name (on W-9) is required', required), maxLength: maxLength(100), minLength: minLength(2) },
                    businessTypeCode: {
                        required: helpers.withMessage('Business Type is required', required),
                        validApptTypeInd: helpers.withMessage('Individual\'s Appointment Type requires "Individual or Sole-Proprietorship"\'s Type Of Business value', this.typeOfBusInd),
                        validApptTypeCorp: helpers.withMessage('Corporate\'s Appointment Type requires "C Corporation" or "S Corporation"\'s Type Of Business value', this.typeOfBusCorp)
                    }
              },
                stateFeeQuestion: {
                   answer: helpers.withMessage('State Fee Question is Required', this.stateFeeQuestion),
                   valid: helpers.withMessage('At least one State Fee needs to be selected', this.stateFeeQuestionValid)
                },
            }
        },
        homeAddress: {
            address1: { required: helpers.withMessage('Home Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
            city: { required: helpers.withMessage('Home City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
            stateCode: { required: helpers.withMessage('Home State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
            zipCode: { required: helpers.withMessage('Home ZipCode is required', required), numeric, maxLength: maxLength(10), minLength: minLength(5) }
        },
        bizAddress: {
            address1: { required: helpers.withMessage('Business Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
            city: { required: helpers.withMessage('Business City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
            stateCode: { required: helpers.withMessage('Business State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
            zipCode: { required: helpers.withMessage('Business ZipCode is required', required), numeric, maxLength: maxLength(10), minLength: minLength(5) },
        }
    }

    #RepresentativeValConfig = {
        a: {
            appointment: {
                personal: {
                    lastName: { required: helpers.withMessage('Last Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
                    firstName: { required: helpers.withMessage('First Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
                    middleName: { maxLength: maxLength(1) },
                    ssn: { required: helpers.withMessage('SSN is required', required), numeric, maxLength: maxLength(9), minLength: minLength(9) },
                    birthDate: { required: helpers.withMessage('Birth Date is required', required), maxValue: helpers.withMessage('Birth Date is out of range', value => value < new Date().toISOString()) },
                    emailAddress: { required: helpers.withMessage('Email Address is required', required), email },
                    nationalProducerNumber: { required: helpers.withMessage('NPN is required', required), maxLength: maxLength(25), minLength: minLength(1) },
                    residentStateInsuranceLicenseNumber: { required: helpers.withMessage('License Number is required', required), maxLength: maxLength(25), minLength: minLength(1) },
                    cellNumber: { required: helpers.withMessage('Phone Number is required', required), numeric, maxLength: maxLength(10), minLength: minLength(10) },
                    businessCellNumber: { numeric, maxLength: maxLength(10), minLength: minLength(10) },
                    sendCorrespondenceToCode: { required: helpers.withMessage('Correspondence selection is required', required) }
                },
                states: { required: helpers.withMessage('At least one state must be chosen', required), minLength: minLength(1) },
                answers: { required: helpers.withMessage('All questions must be answered', this.allAnswered(15)) },
                appUserDocuments: {
                    hasDoc2: helpers.withMessage('Missing E&O Certificate', this.containsDoc('DOCTYP_EO_CERT'))
                },
                signatures: {
                    hasSigQA: helpers.withMessage('Q&A Signature Required', this.containsSig('SIGTYP_QNA')),
                    hasSigSched: helpers.withMessage('Commission Sched Signature Required', this.containsSig('SIGTYP_COMSCHED')),
                    hasSigFees: helpers.withMessage('Fees Signature Required', this.containsSig('SIGTYP_ST_FEES')),
                    hasSigPolicy: helpers.withMessage('Policy Acknowledge checkbox Required', this.containsSig('SIGTYP_POLICY_AGREE')),
                    hasSigRep: helpers.withMessage('Representative Agreement checkbox Required', this.containsSig('SIGTYP_ACKREP')),
                    hasSigAML: helpers.withMessage('AML Acknowledge checkbox Required', this.containsSig('SIGTYP_AML')),
                    hasSigSchedDt: helpers.withMessage('Commission Sign Date is Required', this.containsSig('SIGTYP_COMSCHED_DT')),
                    hasSigAgt: helpers.withMessage('Commission Agent Name is Required', this.containsSig('SIGTYP_COMSCHED_AGT')),
                    hasSigName: helpers.withMessage('AML: Your Name Required', this.containsSig('SIGTYP_AGREENAME')),
                    hasSigAgree: helpers.withMessage('Agreement Signature Required', this.containsSig('SIGTYP_AGREESIG'))
                },
                commissionRemitAssign: {
                    agentAgencyName: { required: helpers.withMessage('Agency Name is required', required), maxLength: maxLength(100), minLength: minLength(2) },
                    address1: { required: helpers.withMessage('Agency Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
                    city: { required: helpers.withMessage('Agency City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
                    stateCode: { required: helpers.withMessage('Agency State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
                    zip: { required: helpers.withMessage('Agency ZipCode is required', required), maxLength: maxLength(15), minLength: minLength(5) },
                    ssnTaxID: {
                        required: helpers.withMessage('SSN or Tax ID is required', required), maxLength: helpers.withMessage('SSN or Tax ID must be exactly 9 digits', maxLength(9)),
                        minLength: helpers.withMessage('SSN or Tax ID must be exactly 9 digits', minLength(9))
                    }
                },
                ccInfo: {
                    creditCardTypeCode: { required: helpers.withMessage('Credit Card Type is required', required) },
                    cardNumber: { required: helpers.withMessage('Credit Card Number is required', required), maxLength: maxLength(19), minLength: minLength(13) },
                    expirationMonth: { required: helpers.withMessage('Credit Card expiration month is required', required) },
                    expirationYear: { required: helpers.withMessage('Credit Card expiration year is required', required) },
                    cvcNumber: { required: helpers.withMessage('Credit Card CVC number is required', required), maxLength: maxLength(4), minLength: minLength(3) },
                    nameonCard: { required: helpers.withMessage('Name on Credit Card is required', required), maxLength: maxLength(150), minLength: minLength(2) },
                    billingAddress: { required: helpers.withMessage('CC Billing Address is required', required), maxLength: maxLength(150), minLength: minLength(2) },
                    city: { required: helpers.withMessage('CC City is required', required), maxLength: maxLength(150), minLength: minLength(2) },
                    stateCode: { required: helpers.withMessage('CC State is required', required), maxLength: maxLength(150), minLength: minLength(2) },
                    zip: { required: helpers.withMessage('CC Zip Code is required', required), maxLength: maxLength(150), minLength: minLength(2) },
              },
                stateFeeQuestion: {
                   answer: helpers.withMessage('State Fee Question is Required', this.stateFeeQuestion),
                   valid: helpers.withMessage('At least one State Fee needs to be selected', this.stateFeeQuestionValid)
                },
            }
        },
        homeAddress: {
            address1: { required: helpers.withMessage('Home Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
            city: { required: helpers.withMessage('Home City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
            stateCode: { required: helpers.withMessage('Home State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
            zipCode: { required: helpers.withMessage('Home ZipCode is required', required), numeric, maxLength: maxLength(10), minLength: minLength(5) }
        },
        bizAddress: {
            address1: { maxLength: maxLength(60), minLength: minLength(2) },
            city: { maxLength: maxLength(100), minLength: minLength(2) },
            stateCode: { maxLength: maxLength(2), minLength: minLength(2) },
            zipCode: { numeric, maxLength: maxLength(10), minLength: minLength(5) }
        }
    }

   #PrincipalValConfig = {
      a: {
         appointment: {
            personal: {
               lastName: { required: helpers.withMessage('Last Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
               firstName: { required: helpers.withMessage('First Name is required', required), maxLength: maxLength(50), minLength: minLength(2) },
               middleName: { maxLength: maxLength(1) },
               ssn: { required: helpers.withMessage('SSN is required', required), numeric, maxLength: maxLength(9), minLength: minLength(9) },
               birthDate: { required: helpers.withMessage('Birth Date is required', required), maxValue: helpers.withMessage('Birth Date is out of range', value => value < new Date().toISOString()) },
               emailAddress: { required: helpers.withMessage('Email Address is required', required), email },
               nationalProducerNumber: { required: helpers.withMessage('NPN is required', required), maxLength: maxLength(25), minLength: minLength(1) },
               residentStateInsuranceLicenseNumber: { required: helpers.withMessage('License Number is required', required), maxLength: maxLength(25), minLength: minLength(1) },
               cellNumber: { required: helpers.withMessage('Phone Number is required', required), numeric, maxLength: maxLength(10), minLength: minLength(10) },
               businessCellNumber: { numeric, maxLength: maxLength(10), minLength: minLength(10) },
               sendCorrespondenceToCode: { required: helpers.withMessage('Correspondence selection is required', required) }
            },
            answers: { required: helpers.withMessage('All questions must be answered', this.allAnswered(15)) },
            signatures: {
               hasSigQA: helpers.withMessage('Q&A Signature Required', this.containsSig('SIGTYP_QNA')),
               hasSigSched: helpers.withMessage('Commission Sched Signature Required', this.containsSig('SIGTYP_COMSCHED')),
               hasSigFees: helpers.withMessage('Fees Signature Required', this.containsSig('SIGTYP_ST_FEES')),
               hasSigPolicy: helpers.withMessage('Policy Acknowledge checkbox Required', this.containsSig('SIGTYP_POLICY_AGREE')),
               hasSigAgent: helpers.withMessage('Agent Agreement checkbox Required', this.containsSig('SIGTYP_AGENT_AGREE')),
               hasSigRep: helpers.withMessage('Representative Agreement checkbox Required', this.containsSig('SIGTYP_ACKREP')),
               hasSigAML: helpers.withMessage('AML Acknowledge checkbox Required', this.containsSig('SIGTYP_AML')),
               hasSigSchedDt: helpers.withMessage('Commission Sign Date is Required', this.containsSig('SIGTYP_COMSCHED_DT')),
               hasSigAgt: helpers.withMessage('Commission Agent Name is Required', this.containsSig('SIGTYP_COMSCHED_AGT')),
               hasSigName: helpers.withMessage('AML: Your Name Required', this.containsSig('SIGTYP_AGREENAME')),
               hasSigAgree: helpers.withMessage('Agreement Signature Required', this.containsSig('SIGTYP_AGREESIG'))
            },
            commissionRemitAssign: {
               agentAgencyName: { required: helpers.withMessage('Agency Name is required', required), maxLength: maxLength(100), minLength: minLength(2) },
               address1: { required: helpers.withMessage('Agency Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
               city: { required: helpers.withMessage('Agency City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
               stateCode: { required: helpers.withMessage('Agency State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
               zip: { required: helpers.withMessage('Agency ZipCode is required', required), maxLength: maxLength(15), minLength: minLength(5) },
               ssnTaxID: {
                  required: helpers.withMessage('SSN or Tax ID is required', required), maxLength: helpers.withMessage('SSN or Tax ID must be exactly 9 digits', maxLength(9)),
                  minLength: helpers.withMessage('SSN or Tax ID must be exactly 9 digits', minLength(9))
               }
            },
         }
      },
      homeAddress: {
         address1: { required: helpers.withMessage('Home Address is required', required), maxLength: maxLength(60), minLength: minLength(2) },
         city: { required: helpers.withMessage('Home City is required', required), maxLength: maxLength(100), minLength: minLength(2) },
         stateCode: { required: helpers.withMessage('Home State is required', required), maxLength: maxLength(2), minLength: minLength(2) },
         zipCode: { required: helpers.withMessage('Home ZipCode is required', required), numeric, maxLength: maxLength(10), minLength: minLength(5) }
      },
      bizAddress: {
         address1: { maxLength: maxLength(60), minLength: minLength(2) },
         city: { maxLength: maxLength(100), minLength: minLength(2) },
         stateCode: { maxLength: maxLength(2), minLength: minLength(2) },
         zipCode: { numeric, maxLength: maxLength(10), minLength: minLength(5) }
      }
   }
}

export const removeCommission = (fieldService) => {
    fieldService.completion.comms = fieldService.completion.comms - 3;
    delete fieldService.config.a.appointment.signatures.hasSigSchedDt;
    delete fieldService.config.a.appointment.signatures.hasSigAgt;
    delete fieldService.config.a.appointment.signatures.hasSigSched;
}

export const removeStateFees = (fieldService) => {
    delete fieldService.completion.fees;
    delete fieldService.config.a.appointment.signatures.hasSigFees;
    delete fieldService.config.a.appointment.ccInfo;
    delete fieldService.config.a.appointment.states;
}